// Configuração de temas mensais
export interface ThemeConfig {
  theme: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
    button: string;
    buttonHover: string;
  };
  gradients: {
    main: string;
    light: string;
    brand: string;
    button: string;
    buttonHover: string;
  };
  messages: {
    title: string;
    subtitle: string;
    banner: string;
    motivational: string[];
  };
  decorations: {
    emoji: string;
    icon: string;
  };
}

// ====== ANO DINÂMICO ======
const CURRENT_YEAR = new Date().getFullYear();
const NEXT_YEAR = CURRENT_YEAR + 1;

export const monthlyThemes: { [key: string]: ThemeConfig } = {
  // Setembro Amarelo - Prevenção ao Suicídio
  september: {
    theme: "yellow",
    colors: {
      primary: "bg-yellow-600",
      secondary: "bg-yellow-700",
      accent: "text-yellow",
      background: "from-yellow-50 to-amber-50",
      text: "text-yellow-800",
      button: "bg-yellow-500 hover:bg-yellow-600",
      buttonHover: "bg-yellow-600",
    },
    gradients: {
      main: "from-yellow-800 to-amber-700",
      light: "from-yellow-60 to-amber-100",
      brand: "from-yellow-700 to-amber-800",
      button: "from-yellow-600 to-amber-700",
      buttonHover: "from-yellow-700 to-amber-800",
    },
    messages: {
      title: "Setembro Amarelo",
      subtitle: "Valorização da Vida e Prevenção ao Suicídio",
      banner: "💛 A vida é nossa maior conquista!",
      motivational: [
        "Sua vida importa! 💛",
        "Você não está sozinho, estamos aqui por você!",
        "Falar é a melhor solução - busque ajuda!",
        "Seja a luz na vida de alguém hoje! ✨",
        "Cada vida importa, cada história vale a pena!",
        "A ajuda certa pode fazer toda a diferença!",
        "Você é mais forte do que imagina! 💪",
        "Tempos difíceis não duram para sempre!",
        "Compartilhe suas dores, divida suas esperanças!",
      ],
    },
    decorations: {
      emoji: "💛",
      icon: "🌻",
    },
  },

  // Outubro Rosa - Conscientização do Câncer de Mama
  october: {
    theme: "pink",
    colors: {
      primary: "bg-pink-500",
      secondary: "bg-pink-600",
      accent: "text-pink",
      background: "from-pink-50 to-rose-50",
      text: "text-pink-800",
      button: "bg-pink-500 hover:bg-pink-600",
      buttonHover: "bg-pink-600",
    },
    gradients: {
      main: "from-pink-500 to-rose-600",
      light: "from-pink-50 to-rose-100",
      brand: "from-pink-700 to-rose-800",
      button: "from-pink-600 to-rose-700",
      buttonHover: "from-pink-700 to-rose-800",
    },
    messages: {
      title: "Outubro Rosa",
      subtitle: "Conscientização sobre o Câncer de Mama",
      banner: "💖 A prevenção é um ato de amor próprio!",
      motivational: [
        "Cuide-se com amor! 💕",
        "A prevenção salva vidas!",
        "Toque de amor pela vida!",
        "Seu corpo, sua prioridade! ✨",
        "Mulher que se ama, se cuida!",
        "O autoexame é um gesto de carinho!",
        "A saúde em primeiro lugar!",
        "Prevenir é viver com qualidade!",
        "Você é especial, cuide-se! 🌸",
      ],
    },
    decorations: {
      emoji: "💖",
      icon: "🎀",
    },
  },

  // Novembro Azul - Conscientização da Saúde Masculina
  november: {
    theme: "blue",
    colors: {
      primary: "bg-blue-600",
      secondary: "bg-blue-700",
      accent: "text-blue",
      background: "from-blue-50 to-sky-50",
      text: "text-blue-800",
      button: "bg-blue-600 hover:bg-blue-700",
      buttonHover: "bg-blue-700",
    },
    gradients: {
      main: "from-blue-500 to-sky-700",
      light: "from-blue-50 to-sky-100",
      brand: "from-blue-700 to-sky-800",
      button: "from-blue-600 to-sky-700",
      buttonHover: "from-blue-700 to-sky-800",
    },
    messages: {
      title: "Novembro Azul",
      subtitle: "Cuidando da Saúde Masculina",
      banner: "💙 Cuidar da saúde também é coisa de homem!",
      motivational: [
        "Homem que se cuida vive mais! 💙",
        "Prevenção é atitude de homem consciente!",
        "Sua família precisa de você saudável! 👨‍👩‍👧‍👦",
        "Não deixe para depois, cuide-se agora!",
        "Saúde não tem gênero - cuide-se! ✨",
        "Exames periódicos salvam vidas!",
        "Seja herói da sua própria saúde! 🦸‍♂️",
        "Cuidar da saúde é demonstração de força!",
        "Homem moderno cuida do corpo e da mente!",
        "Viva mais e melhor - previna-se! 🌊",
      ],
    },
    decorations: {
      emoji: "💙",
      icon: "🔵",
    },
  },

  // Dezembro - Natal (1-24 de dezembro)
  december: {
    theme: "red",
    colors: {
      primary: "bg-red-600",
      secondary: "bg-red-700",
      accent: "text-red",
      background: "from-red-50 to-red-50",
      text: "text-red-800",
      button: "bg-red-600 hover:bg-red-700",
      buttonHover: "bg-red-700",
    },
    gradients: {
      main: "from-red-500 to-red-600",
      light: "from-red-50 to-red-100",
      brand: "from-red-700 to-red-800",
      button: "from-red-600 to-red-700",
      buttonHover: "from-red-700 to-red-800",
    },
    messages: {
      title: "Dezembro",
      subtitle: "Celebrando o Natal e a Vida",
      banner: "🎄 Feliz Natal! Que a paz esteja em seu coração!",
      motivational: [
        "Que a paz do Natal esteja em seu coração! 🕊️",
        "Tempo de celebrar a vida e as conquistas!",
        "Que a luz do Natal ilumine seus caminhos! 💫",
        "Desejamos saúde e felicidade para você!",
        "Celebre o amor e a união! ❤️",
        "Natal é tempo de renovar as esperanças! ✨",
        "Que o espírito natalino ilumine seu lar! 🏠",
        "Momento de gratidão e reflexão! 🙏",
        "Feliz Natal e muitas bênçãos! 🌟",
        "Que esta data seja repleta de alegria! 🎁",
      ],
    },
    decorations: {
      emoji: "🎄",
      icon: "⭐",
    },
  },

  // Ano Novo (25-31 de dezembro)
  newyear: {
    theme: "gold",
    colors: {
      primary: "bg-blue-600",
      secondary: "bg-blue-700",
      accent: "text-blue",
      background: "from-blue-50 to-amber-50",
      text: "text-blue-800",
      button: "bg-blue-600 hover:bg-blue-700",
      buttonHover: "bg-blue-700",
    },
    gradients: {
      main: "from-blue-500 to-blue-600",
      light: "from-blue-50 to-blue-100",
      brand: "from-blue-700 to-blue-800",
      button: "from-blue-600 to-blue-700",
      buttonHover: "from-blue-700 to-blue-800",
    },
    messages: {
      title: `Ano Novo ${NEXT_YEAR}`,
      subtitle: `Feliz ${NEXT_YEAR}! Que seja um ano de saúde e prosperidade!`,
      banner: `🎆 ${NEXT_YEAR} - Novos Horizontes, Novas Conquistas! 🎇`,
      motivational: [
        `Feliz ${NEXT_YEAR}! Que seja repleto de saúde! 🎊`,
        "Novo ano, novas energias para cuidar da sua saúde! 💫",
        `Que ${NEXT_YEAR} traga muita paz e bem-estar! ✨`,
        "Saúde é o melhor presente para o novo ano! 🎁",
        "Renove suas esperanças para o ano que começa! 🌟",
        `Desejamos um ${NEXT_YEAR} abençoado e saudável! 🙏`,
        "Novo ciclo, novos cuidados com a saúde! 💖",
        `Que ${NEXT_YEAR} seja de muitas conquistas! 🏆`,
        "Celebre a vida e cuide da sua saúde no novo ano! 🎉",
        "Tempo de recomeçar com saúde e alegria! 🥳",
      ],
    },
    decorations: {
      emoji: "🎆",
      icon: "🎇",
    },
  },

  // Default - Para outros meses
  default: {
    theme: "default",
    colors: {
      primary: "bg-blue-700",
      secondary: "bg-blue-800",
      accent: "text-blue",
      background: "from-gray-50 to-blue-50",
      text: "text-gray-800",
      button: "bg-blue-600 hover:bg-blue-700",
      buttonHover: "bg-blue-700",
    },
    gradients: {
      main: "from-black to-black",
      light: "from-blue-50 to-indigo-100",
      brand: "from-blue-900 to-blue-700",
      button: "from-blue-600 to-indigo-700",
      buttonHover: "from-blue-700 to-indigo-800",
    },
    messages: {
      title: "SISCLINIC",
      subtitle: "Sistema de Gestão em Saúde",
      banner: "® Todos os direitos reservados a AFRAFEP Saúde",
      motivational: [
        "Sua saúde em primeiro lugar! 🏥",
        "Cuidando de você com excelência!",
        "Seu bem-estar é nossa prioridade!",
        "Juntos por uma saúde melhor! 👨‍⚕️",
        "Comprometidos com seu cuidado!",
        "Inovação e humanização no atendimento! 🌟",
        "Sempre ao seu dispor!",
        "Cuidar é nossa missão! ❤️",
      ],
    },
    decorations: {
      emoji: "💙",
      icon: "🏥",
    },
  },
};

// Função para obter o tema atual baseado no mês e dia
export const getCurrentTheme = (): ThemeConfig => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  // Verifica se é período de Ano Novo (25 a 31 de dezembro)
  if (month === 12 && day >= 25 && day <= 31) {
    return monthlyThemes.newyear;
  }

  switch (month) {
    case 9:
      return monthlyThemes.september;
    case 10:
      return monthlyThemes.october;
    case 11:
      return monthlyThemes.november;
    case 12:
      return monthlyThemes.december;
    default:
      return monthlyThemes.default;
  }
};

// Função para obter uma mensagem motivacional aleatória
export const getRandomMotivationalMessage = (theme: ThemeConfig): string => {
  const messages = theme.messages.motivational;
  const randomIndex = Math.floor(Math.random() * messages.length);
  return messages[randomIndex];
};

// Verifica se é um mês temático
export const isThemedMonth = (): boolean => {
  const currentTheme = getCurrentTheme();
  return currentTheme.theme !== "default";
};

// Função para obter o texto da campanha atual baseado no tema
export const getCampaignText = (currentTheme: ThemeConfig) => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  const isEarlyMonth = day <= 15;

  // Usa a mesma lógica do switch para determinar o mês temático
  switch (month) {
    case 9: // Setembro Amarelo
      return {
        title: currentTheme.messages.title,
        subtitle: isEarlyMonth
          ? "A valorização da vida começa agora! " + currentTheme.messages.banner
          : "Juntos pela prevenção! " + currentTheme.messages.banner,
        isEarly: isEarlyMonth,
      };

    case 10: // Outubro Rosa
      return {
        title: currentTheme.messages.title,
        subtitle: isEarlyMonth
          ? "A prevenção começa antes! " + currentTheme.messages.banner
          : "Juntos na luta contínua! " + currentTheme.messages.banner,
        isEarly: isEarlyMonth,
      };

    case 11: // Novembro Azul
      return {
        title: currentTheme.messages.title,
        subtitle: isEarlyMonth
          ? "A prevenção começa agora! " + currentTheme.messages.banner
          : "Cuidar da saúde é um compromisso! " + currentTheme.messages.banner,
        isEarly: isEarlyMonth,
      };

    case 12: // Dezembro
      if (day >= 25 && day <= 31) {
        // Ano Novo (25-31 de dezembro)
        return {
          title: currentTheme.messages.title,
          subtitle: currentTheme.messages.banner,
          isEarly: false,
        };
      } else {
        // Natal (1-24 de dezembro)
        return {
          title: "Natal",
          subtitle: isEarlyMonth
            ? "Celebrando a vida e preparando o espírito natalino! 🎄"
            : "Que a paz do Natal esteja com você! ⭐",
          isEarly: isEarlyMonth,
        };
      }

    default:
      return {
        title: currentTheme.messages.title,
        subtitle: currentTheme.messages.subtitle,
        isEarly: false,
      };
  }
};